import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import styles from "./AutoFiEnrollmentModal.module.scss";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import AccessManagementClient from "../../../clients/AccessManagementClient";

export const AUTOFI_KDM_QUERY_KEY = "autofiKdmList";

const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+\.[^\s@.]+$/;

const AutoFiEnrollmentModal = ({
  open,
  onClose,
  onSubmit,
  dealerCommonId,
  hasSavedRouteOneId,
  routeOneId,
  isFordBrand,
}) => {
  const { t } = useTranslation();
  const [selectedKdm, setSelectedKdm] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    data: kdmList = [],
    isLoading: isKdmLoading,
    isError: isKdmError,
  } = useQuery({
    queryKey: [AUTOFI_KDM_QUERY_KEY, dealerCommonId],
    queryFn: () => AccessManagementClient.getDealerRoles(dealerCommonId),
    enabled: open && !!dealerCommonId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!open) {
      setSelectedKdm("");
      setEmail("");
      setEmailError("");
      setIsSuccess(false);
    }
  }, [open]);

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (val && !EMAIL_REGEX.test(val)) {
      setEmailError(
        t(
          "DealerOnboarding.content.thirdPartyFinancing.modal.emailValidationError"
        )
      );
    } else {
      setEmailError("");
    }
  };

  const isSubmitDisabled =
    !selectedKdm ||
    !email ||
    !!emailError ||
    !hasSavedRouteOneId ||
    !routeOneId;

  const handleSubmit = async () => {
    const selectedKdmData = kdmList.find((k) => k.userId === selectedKdm);
    if (!selectedKdmData) return;

    try {
      await Promise.resolve(
        onSubmit({ kdm: selectedKdmData, email, routeOneId })
      );
      setIsSuccess(true);
    } catch (error) {
      console.error("Failed to submit AutoFi enrollment:", error);
    }
  };

  const getKdmDisplayName = (kdm) =>
    kdm.displayName ||
    `${kdm.firstName ?? ""} ${kdm.lastName ?? ""}`.trim() ||
    kdm.userId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      data-testid="autofi-enrollment-modal"
      PaperProps={{ className: styles.modalPaper }}
    >
      <IconButton
        onClick={onClose}
        className={styles.closeButton}
        data-testid="autofi-modal-close"
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {isSuccess ? (
        <>
          <DialogTitle className={styles.modalTitleContainer}>
            <Typography className={styles.modalTitle}>
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.modal.successTitle"
              )}
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.modalContent}>
            <Typography className={styles.descriptionText}>
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.modal.successBody"
              )}
            </Typography>
          </DialogContent>

          <DialogActions className={styles.modalActions}>
            <EMPButton
              variant="contained"
              onClick={onClose}
              isFord={isFordBrand}
              testId="autofi-modal-close-success"
              sx={{
                width: "auto !important",
                minWidth: "unset !important",
                height: "auto",
                padding: "10px 20px",
                whiteSpace: "nowrap",
              }}
            >
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.modal.closeButton"
              )}
            </EMPButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle className={styles.modalTitleContainer}>
            <Typography className={styles.modalTitle}>
              {t("DealerOnboarding.content.thirdPartyFinancing.modal.title")}
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.modalContent}>
            <Box className={styles.introduction}>
              <Typography className={styles.descriptionText}>
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.modal.bodyPre"
                )}{" "}
                <strong>
                  {t(
                    "DealerOnboarding.content.thirdPartyFinancing.modal.bodyBold"
                  )}
                </strong>{" "}
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.modal.bodyPost"
                )}
              </Typography>
            </Box>

            {isKdmError && (
              <Box className={styles.errorAlert} data-testid="autofi-kdm-error">
                <Box className={styles.errorAlertHeader}>
                  <ErrorOutlineIcon className={styles.errorAlertIcon} />
                  <Typography className={styles.errorAlertTitle}>
                    {t(
                      "DealerOnboarding.content.thirdPartyFinancing.modal.kdmErrorTitle"
                    )}
                  </Typography>
                </Box>
                <Typography className={styles.errorAlertBody}>
                  {t(
                    "DealerOnboarding.content.thirdPartyFinancing.modal.kdmErrorBody"
                  )}
                </Typography>
              </Box>
            )}

            <Box className={styles.fieldRow}>
              <Typography
                component="label"
                htmlFor="autofi-kdm-select"
                className={styles.fieldLabel}
              >
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.modal.kdmLabel"
                )}
              </Typography>

              {isKdmLoading ? (
                <CircularProgress size={24} data-testid="autofi-kdm-loading" />
              ) : (
                <FormControl>
                  <Select
                    id="autofi-kdm-select"
                    value={selectedKdm}
                    onChange={(e) => setSelectedKdm(e.target.value)}
                    displayEmpty
                    className={styles.field}
                    inputProps={{
                      "data-testid": "autofi-kdm-select",
                      "aria-label": t(
                        "DealerOnboarding.content.thirdPartyFinancing.modal.kdmLabel"
                      ),
                    }}
                  >
                    <MenuItem value="">
                      <span className={styles.placeholderText}>
                        {t(
                          "DealerOnboarding.content.thirdPartyFinancing.modal.kdmPlaceholder"
                        )}
                      </span>
                    </MenuItem>
                    {kdmList.map((kdm) => (
                      <MenuItem key={kdm.userId} value={kdm.userId}>
                        {getKdmDisplayName(kdm)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Box className={styles.fieldRow}>
              <Typography
                component="label"
                htmlFor="autofi-email-input"
                className={styles.fieldLabel}
              >
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.modal.emailLabel"
                )}
              </Typography>
              <OutlinedInput
                id="autofi-email-input"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                inputProps={{ "data-testid": "autofi-email-input" }}
                className={`${styles.field} ${
                  emailError ? styles.fieldError : ""
                }`}
              />
              {emailError && (
                <Box
                  className={styles.fieldErrorRow}
                  data-testid="autofi-email-error"
                >
                  <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  <Typography className={styles.errorText}>
                    {emailError}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box className={styles.fieldRow}>
              <Typography
                component="label"
                htmlFor="autofi-routeone-id"
                className={styles.fieldLabel}
              >
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.modal.routeOneLabel"
                )}
              </Typography>
              <OutlinedInput
                id="autofi-routeone-id"
                value={routeOneId && routeOneId !== "UNSET" ? routeOneId : ""}
                disabled={!hasSavedRouteOneId}
                inputProps={{ "data-testid": "autofi-routeone-id" }}
                className={styles.field}
              />
              {!hasSavedRouteOneId && (
                <Box
                  className={styles.fieldErrorRow}
                  data-testid="autofi-routeone-error"
                >
                  <ErrorOutlineIcon className={styles.fieldErrorIcon} />
                  <Typography className={styles.errorText}>
                    {t(
                      "DealerOnboarding.content.thirdPartyFinancing.modal.routeOneNotSetError"
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions className={styles.modalActions}>
            <EMPButton
              variant="outlined"
              onClick={onClose}
              testId="autofi-modal-cancel"
              isFord={false}
              sx={{
                width: "auto !important",
                minWidth: "unset !important",
                height: "auto",
                padding: "10px 20px",
                whiteSpace: "nowrap",
              }}
            >
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.modal.cancelButton"
              )}
            </EMPButton>
            <EMPButton
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              isFord={isFordBrand}
              testId="autofi-modal-submit"
              sx={{
                width: "auto !important",
                minWidth: "unset !important",
                height: "auto",
                padding: "10px 20px",
                whiteSpace: "nowrap",
              }}
            >
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.modal.submitButton"
              )}
            </EMPButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

AutoFiEnrollmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dealerCommonId: PropTypes.string.isRequired,
  hasSavedRouteOneId: PropTypes.bool.isRequired,
  routeOneId: PropTypes.string.isRequired,
  isFordBrand: PropTypes.bool.isRequired,
};

export default AutoFiEnrollmentModal;
